// NumberInput — drop-in wrapper around <input type="number"> that adds
// a tablet-friendly double-click → on-screen numpad behavior. Single
// click leaves the input focused so keyboard entry works exactly like
// a vanilla number input.
//
// Optional `unit` suffix renders inside the input on the right (e.g.
// "in", "$", "hrs") so the operator can see units without us having
// to add a separate <span> at every call site.

import { useState } from 'react';
import { Numpad } from './Numpad';

export interface NumberInputProps {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  placeholder?: string;
  /** Forwarded to <input>, AND used by the Numpad modal. */
  step?: number;
  min?: number;
  max?: number;
  /** Optional suffix shown inside the input (e.g. "in", "$"). */
  unit?: string;
  numpadTitle?: string;
  className?: string;
  disabled?: boolean;
  /** Custom step for the numpad (e.g. 0.0625 for thickness). Defaults to step. */
  numpadStep?: number;
}

export function NumberInput({
  value,
  onChange,
  placeholder,
  step = 1,
  min,
  max,
  unit,
  numpadTitle,
  className,
  disabled,
  numpadStep,
}: NumberInputProps) {
  const [numpadOpen, setNumpadOpen] = useState(false);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    if (raw === '') {
      onChange(undefined);
      return;
    }
    const n = Number.parseFloat(raw);
    if (Number.isFinite(n)) onChange(n);
  }

  function handleDoubleClick() {
    if (disabled) return;
    setNumpadOpen(true);
  }

  function handleCommit(n: number) {
    onChange(n);
    setNumpadOpen(false);
  }

  const baseInputCls =
    'w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange disabled:opacity-50';

  return (
    <>
      <div className="relative">
        <input
          type="number"
          value={value ?? ''}
          onChange={handleChange}
          onDoubleClick={handleDoubleClick}
          placeholder={placeholder}
          step={step}
          min={min}
          max={max}
          disabled={disabled}
          title="Double-click for numpad."
          className={`${baseInputCls} ${unit ? 'pr-8' : ''} ${className ?? ''}`}
        />
        {unit && (
          <span
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-text-mute pointer-events-none select-none"
            aria-hidden
          >
            {unit}
          </span>
        )}
      </div>

      <Numpad
        open={numpadOpen}
        initialValue={value !== undefined ? String(value) : ''}
        title={numpadTitle}
        unit={unit}
        step={numpadStep ?? step}
        min={min}
        max={max}
        onCommit={handleCommit}
        onClose={() => setNumpadOpen(false)}
      />
    </>
  );
}
